import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { ar, enUS, fr } from 'date-fns/locale';
import { toast } from 'sonner';

import { api, dailyAttendanceAPI } from '../services/api';
import { todayStr } from '../utils/attendanceUtils';
import { buildReportDefinitions } from '../components/attendance/attendanceReports';
import { exportReportCSV, exportReportExcel, exportReportPDF } from '../utils/attendanceReportExports';
import useAttendanceFilters from '../hooks/useAttendanceFilters';
import useAttendanceData from '../hooks/useAttendanceData';
import {
  AssignPlanningDialog,
  ManualPunchDialog,
  AbsenceReasonDialog,
  ReportDialog,
} from '../components/attendance/AttendanceDialogs';
import PointagesFiltersCard from '../components/attendance/PointagesFiltersCard';
import PointagesPreviewCard from '../components/attendance/PointagesPreviewCard';
import PointagesResultsSection from '../components/attendance/PointagesResultsSection';

const CalculRapport = () => {
  const { t, i18n } = useTranslation();
  const {
    scope, setScope,
    selectedDeptId, setSelectedDeptId,
    selectedEmpIds, setSelectedEmpIds,
    empSearchQuery, setEmpSearchQuery,
    period,
    dateFrom, setDateFrom,
    dateTo, setDateTo,
    handlePeriodChange: handlePeriodChange_hook,
    navigate: navigate_hook,
    buildFilterParams,
  } = useAttendanceFilters();

  const {
    departments,
    employees,
    schedules: loadedSchedules,
    absenceReasons: loadedAbsenceReasons,
    companyInfo: loadedCompanyInfo,
  } = useAttendanceData(scope, selectedDeptId, selectedEmpIds);

  const [results, setResults]         = useState([]);
  const [resultStats, setResultStats] = useState(null);

  const [calculating, setCalculating]       = useState(false);
  const [hasCalculated, setHasCalculated]   = useState(false);
  const [statusFilter, setStatusFilter]     = useState('all');
  const [tableSearch, setTableSearch]       = useState('');
  const [empPopoverOpen, setEmpPopoverOpen] = useState(false);
  const [viewMode, setViewMode]             = useState('summary');
  const [currentPage, setCurrentPage]       = useState(1);
  const [pageSize, setPageSize]             = useState(50);
  const [exportingPDF, setExportingPDF]     = useState(false);
  const [reportDialog, setReportDialog]     = useState(false);
  const [selectedReportId, setSelectedReportId] = useState('full');

  const schedules      = loadedSchedules;
  const absenceReasons = loadedAbsenceReasons;
  const companyInfo    = loadedCompanyInfo;

  const [assignDialog, setAssignDialog]         = useState(null);
  const [assignScheduleId, setAssignScheduleId] = useState('');
  const [assignStartDate, setAssignStartDate]   = useState(todayStr);
  const [assignEndDate, setAssignEndDate]       = useState(todayStr);
  const [assigning, setAssigning]               = useState(false);

  const [manualPunchDialog, setManualPunchDialog] = useState(null);
  const [manualPunchTime, setManualPunchTime]     = useState('');
  const [manualPunchDir, setManualPunchDir]       = useState('0');
  const [manualPunchReason, setManualPunchReason] = useState('');
  const [addingPunch, setAddingPunch]             = useState(false);

  const [absenceDialog, setAbsenceDialog]       = useState(null);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [assigningAbsence, setAssigningAbsence] = useState(false);

  const [openMenuId, setOpenMenuId] = useState(null);

  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const toggleEmployeeExpand = (id) => setExpandedEmployees(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const dateLocale = useMemo(() => {
    if (i18n.language?.startsWith('ar')) return ar;
    if (i18n.language?.startsWith('en')) return enUS;
    return fr;
  }, [i18n.language]);

  const resetResults = useCallback(() => {
    setHasCalculated(false);
    setResults([]);
    setCurrentPage(1);
  }, []);

  const handlePeriodChange = (p) => handlePeriodChange_hook(p, resetResults);
  const handleNavigate = (dir) => navigate_hook(dir, resetResults);

  const previewEnabled = !!dateFrom && !(scope === 'manual' && !selectedEmpIds.length);
  const previewQ = useQuery({
    queryKey: ['attendance-preview', scope, selectedDeptId, selectedEmpIds, dateFrom, dateTo],
    queryFn: () => {
      const params = { date_from: dateFrom, date_to: dateTo, ...buildFilterParams() };
      return dailyAttendanceAPI.getPreview(params).then((res) => res.data?.employees || []);
    },
    enabled: previewEnabled,
  });

  const previewList = previewQ.data ?? [];
  const loadingPreview = previewQ.isPending && previewEnabled;

  const canCalculate = useMemo(() => {
    if (!dateFrom || !dateTo) return false;
    if (scope === 'manual' && !selectedEmpIds.length) return false;
    return true;
  }, [dateFrom, dateTo, scope, selectedEmpIds]);

  const runCalculation = useCallback(async (overridePayload = null) => {
    if (!canCalculate && !overridePayload) return;
    setCalculating(true);
    if (!overridePayload) setHasCalculated(false);
    try {
      const payload = overridePayload || {
        date_from: dateFrom,
        date_to: dateTo,
        recalculate: true,
        ...(scope === 'department' && selectedDeptId ? { department_id: parseInt(selectedDeptId) } : {}),
        ...(scope === 'manual' && selectedEmpIds.length ? { employee_ids: selectedEmpIds } : {}),
      };

      const res  = await dailyAttendanceAPI.calculate(payload);
      const data = res.data;

      if (overridePayload) {
        setResults(prev => {
          const updated = [...prev];
          (data.records || []).forEach(newRec => {
            const idx = updated.findIndex(
              r => r.employee_id === newRec.employee_id && r.date === newRec.date
            );
            if (idx >= 0) updated[idx] = newRec;
            else updated.push(newRec);
          });
          return updated;
        });
      } else {
        setResults(data.records || []);
        setResultStats({
          total_processed: data.total_processed,
          total_created:   data.total_created,
          total_updated:   data.total_updated,
          total_errors:    data.total_errors,
        });
        setHasCalculated(true);
        setCurrentPage(1);
      }

      const msg = t('pointagesAdvanced.recordsCalculated', { count: data.total_created + data.total_updated });
      if (data.total_errors > 0)
        toast.warning(msg, { description: t('pointagesAdvanced.errorsCount', { count: data.total_errors }) });
      else
        toast.success(t('pointagesAdvanced.calcDone'), { description: msg });
    } catch (err) {
      toast.error(t('pointagesAdvanced.calcError'), {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setCalculating(false);
    }
  }, [canCalculate, dateFrom, dateTo, scope, selectedDeptId, selectedEmpIds, t]);

  const handleCalculate = () => runCalculation();

  const filteredResults = useMemo(() => {
    let list = results;
    if (statusFilter === 'anomalies') {
      const anomalyStatuses = new Set([
        'late', 'early_departure', 'late_early_departure',
        'absent', 'absence_justifiee', 'missing_exit', 'no_schedule',
      ]);
      list = list.filter(r => anomalyStatuses.has(r.status));
    } else if (statusFilter !== 'all') {
      list = list.filter(r => r.status === statusFilter);
    }
    if (tableSearch.trim()) {
      const q = tableSearch.toLowerCase();
      list = list.filter(r =>
        r.registration_number?.toLowerCase().includes(q) ||
        r.last_name?.toLowerCase().includes(q) ||
        r.first_name?.toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      const nameA = `${a.last_name} ${a.first_name}`;
      const nameB = `${b.last_name} ${b.first_name}`;
      if (nameA !== nameB) return nameA.localeCompare(nameB, i18n.language);
      return a.date.localeCompare(b.date);
    });
  }, [results, statusFilter, tableSearch, i18n.language]);

  useEffect(() => { setCurrentPage(1); }, [filteredResults.length, statusFilter, tableSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredResults.length / pageSize));
  const paginatedResults = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredResults.slice(start, start + pageSize);
  }, [filteredResults, currentPage, pageSize]);

  const summaryRows = useMemo(() => {
    const map = {};
    filteredResults.forEach(r => {
      if (!map[r.employee_id]) {
        map[r.employee_id] = {
          employee_id: r.employee_id,
          registration_number: r.registration_number,
          last_name: r.last_name,
          first_name: r.first_name,
          days: 0, present: 0, absent: 0, late: 0,
          early: 0, missing_exit: 0, no_schedule: 0,
          public_holiday: 0, day_off: 0,
          worked: 0, expected: 0, lateness: 0, overtime: 0,
          day_records: [],
        };
      }
      const s = map[r.employee_id];
      s.days++;
      s.day_records.push(r);
      if (r.status === 'present')               s.present++;
      else if (r.status === 'absent')            s.absent++;
      else if (r.status === 'absence_justifiee') s.absent++;
      else if (r.status === 'late')              { s.late++; s.present++; }
      else if (r.status === 'early_departure')   { s.early++; s.present++; }
      else if (r.status === 'late_early_departure') { s.late++; s.early++; s.present++; }
      else if (r.status === 'missing_exit')      { s.missing_exit++; s.present++; }
      else if (r.status === 'no_schedule')       s.no_schedule++;
      else if (r.status === 'public_holiday')    s.public_holiday++;
      else if (r.status === 'day_off')           s.day_off++;
      s.worked   += r.worked_hours   || 0;
      s.expected += r.expected_hours || 0;
      s.lateness += r.lateness_minutes || 0;
      s.overtime += r.overtime_hours || 0;
    });
    return Object.values(map).sort((a, b) => a.last_name.localeCompare(b.last_name, i18n.language));
  }, [filteredResults, i18n.language]);

  const filteredEmployeesForPopover = useMemo(() => {
    if (!empSearchQuery.trim()) return employees;
    const q = empSearchQuery.toLowerCase();
    return employees.filter(e =>
      e.first_name?.toLowerCase().includes(q) ||
      e.last_name?.toLowerCase().includes(q) ||
      e.registration_number?.toLowerCase().includes(q)
    );
  }, [employees, empSearchQuery]);

  const toggleEmployee = (id) => {
    setSelectedEmpIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    resetResults();
  };

  const selectedEmployeeNames = useMemo(() => {
    if (!selectedEmpIds.length) return null;
    const found = employees.filter(e => selectedEmpIds.includes(e.id));
    if (found.length === 1) return `${found[0].first_name} ${found[0].last_name}`;
    return t('pointagesAdvanced.employeesCount', { count: found.length });
  }, [selectedEmpIds, employees, t]);

  const dateRangeLabel = useMemo(() => {
    if (!dateFrom) return '';
    if (dateFrom === dateTo)
      return format(parseISO(dateFrom), 'EEEE d MMMM yyyy', { locale: dateLocale });
    return `${format(parseISO(dateFrom), 'd MMM', { locale: dateLocale })} → ${format(parseISO(dateTo), 'd MMM yyyy', { locale: dateLocale })}`;
  }, [dateFrom, dateTo, dateLocale]);

  const dayCount = useMemo(() => {
    if (!dateFrom || !dateTo) return 1;
    return Math.round((parseISO(dateTo) - parseISO(dateFrom)) / 86400000) + 1;
  }, [dateFrom, dateTo]);

  const statusCounts = useMemo(() => {
    const counts = {};
    results.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [results]);

  // ── Leave data (for the "État des congés" report) ─────────────────────────
  const resultEmployeeIds = useMemo(
    () => [...new Set(results.map(r => r.employee_id))],
    [results]
  );
  const congesQ = useQuery({
    queryKey: ['attendance-conges', dateFrom, dateTo],
    queryFn: async () => {
      try {
        const res = await api.get('v1/absences', { params: { start_to: dateTo, limit: 200 } });
        return res.data?.items || [];
      } catch {
        return []; // missing permission or no data → empty report
      }
    },
    enabled: reportDialog && hasCalculated && !!dateFrom,
  });
  const congesData = useMemo(() => {
    const items = congesQ.data || [];
    const empSet = new Set(resultEmployeeIds);
    return items.filter(a =>
      (empSet.size === 0 || empSet.has(a.employee_id)) &&
      (!dateFrom || !a.end_date || a.end_date >= dateFrom)
    );
  }, [congesQ.data, resultEmployeeIds, dateFrom]);

  // ── Report catalog (derived from the full calculation) ────────────────────
  const reports = useMemo(() => buildReportDefinitions({
    results,
    absenceReasons,
    congesData,
    dateLocale,
  }), [results, absenceReasons, congesData, dateLocale]);

  const reportMeta = useMemo(
    () => ({ companyInfo, dateRangeLabel, dateFrom, dateTo }),
    [companyInfo, dateRangeLabel, dateFrom, dateTo]
  );

  const handleExportCSV = (report) => exportReportCSV(report, reportMeta);
  const handleExportExcel = (report) => exportReportExcel(report, reportMeta);
  const handleExportPDF = async (report) => {
    setExportingPDF(true);
    try {
      await exportReportPDF(report, reportMeta);
      toast.success(t('pointagesAdvanced.pdfExported'));
    } catch (err) {
      toast.error(t('pointagesAdvanced.pdfError'), { description: err.message });
    } finally {
      setExportingPDF(false);
    }
  };

  const openAssignDialog = (row) => {
    setAssignDialog({ employee_id: row.employee_id, first_name: row.first_name, last_name: row.last_name, date: row.date });
    setAssignScheduleId('');
    setAssignStartDate(row.date);
    setAssignEndDate(row.date);
  };

  const handleAssign = async () => {
    if (!assignScheduleId || !assignDialog) return;
    setAssigning(true);
    try {
      await api.post('v1/schedule-assignments/?check_conflicts=false', {
        employee_id: assignDialog.employee_id,
        schedule_id: parseInt(assignScheduleId),
        start_date: assignStartDate,
        end_date: assignEndDate || assignStartDate,
      });
      toast.success(t('pointagesAdvanced.planningAssigned', { name: `${assignDialog.first_name} ${assignDialog.last_name}` }));
      setAssignDialog(null);
      await runCalculation({
        date_from: dateFrom,
        date_to: dateTo,
        employee_ids: [assignDialog.employee_id],
        recalculate: true,
      });
    } catch (err) {
      toast.error(t('pointagesAdvanced.assignError'), {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setAssigning(false);
    }
  };

  const openManualPunchDialog = (row) => {
    setManualPunchDialog({ employee_id: row.employee_id, first_name: row.first_name, last_name: row.last_name, date: row.date });
    setManualPunchTime('');
    setManualPunchDir('0');
    setManualPunchReason('');
  };

  const handleAddManualPunch = async () => {
    if (!manualPunchTime || !manualPunchDialog) return;
    setAddingPunch(true);
    try {
      await api.post('v1/daily-attendance/punches/manual', {
        employee_id: manualPunchDialog.employee_id,
        date: manualPunchDialog.date,
        time: manualPunchTime,
        direction: parseInt(manualPunchDir),
        reason: manualPunchReason || null,
      });
      toast.success(t('pointagesAdvanced.manualPunchAdded'));
      setManualPunchDialog(null);
      await runCalculation({
        date_from: manualPunchDialog.date,
        date_to: manualPunchDialog.date,
        employee_ids: [manualPunchDialog.employee_id],
        recalculate: true,
      });
    } catch (err) {
      toast.error(t('pointagesAdvanced.addPunchError'), {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setAddingPunch(false);
    }
  };

  const openAbsenceDialog = (row) => {
    setAbsenceDialog({ id: row.id, first_name: row.first_name, last_name: row.last_name, date: row.date });
    setSelectedReasonId(row.absence_reason_id ? String(row.absence_reason_id) : '');
  };

  const handleAssignAbsenceReason = async () => {
    if (!absenceDialog) return;
    setAssigningAbsence(true);
    try {
      await api.post('v1/daily-attendance/assign-absence-reason', {
        daily_attendance_id: absenceDialog.id,
        absence_reason_id: selectedReasonId ? parseInt(selectedReasonId) : null,
      });
      toast.success(t('pointagesAdvanced.absenceReasonUpdated'));
      const reason = absenceReasons.find(r => String(r.id) === selectedReasonId);
      setResults(prev => prev.map(r => {
        if (r.id !== absenceDialog.id) return r;
        const wasAbsent   = r.status === 'absent' || r.status === 'missing_exit';
        const wasJustified = r.status === 'absence_justifiee';
        let newStatus = r.status;
        let newStatusColor = r.status_color;
        if (reason && wasAbsent)       { newStatus = 'absence_justifiee'; newStatusColor = 'blue'; }
        else if (!reason && wasJustified) { newStatus = 'absent'; newStatusColor = 'red'; }
        return {
          ...r,
          absence_reason_id:    reason ? reason.id : null,
          absence_reason_name:  reason ? reason.name : null,
          absence_reason_color: reason ? reason.color : null,
          status:       newStatus,
          status_color: newStatusColor,
        };
      }));
      setAbsenceDialog(null);
    } catch (err) {
      toast.error(t('pointagesAdvanced.assignReasonError'), {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setAssigningAbsence(false);
    }
  };

  return (
    <div className="space-y-6">

      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('pointagesAdvanced.title')}</h1>
        <p className="text-muted-foreground">{t('pointagesAdvanced.subtitle')}</p>
      </div>

      <PointagesFiltersCard
        scope={scope}
        setScope={setScope}
        selectedDeptId={selectedDeptId}
        setSelectedDeptId={setSelectedDeptId}
        departments={departments}
        empPopoverOpen={empPopoverOpen}
        setEmpPopoverOpen={setEmpPopoverOpen}
        selectedEmployeeNames={selectedEmployeeNames}
        filteredEmployeesForPopover={filteredEmployeesForPopover}
        selectedEmpIds={selectedEmpIds}
        setSelectedEmpIds={setSelectedEmpIds}
        empSearchQuery={empSearchQuery}
        setEmpSearchQuery={setEmpSearchQuery}
        toggleEmployee={toggleEmployee}
        period={period}
        handlePeriodChange={handlePeriodChange}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        dateRangeLabel={dateRangeLabel}
        dayCount={dayCount}
        handleNavigate={handleNavigate}
        loadingPreview={loadingPreview}
        previewList={previewList}
        canCalculate={canCalculate}
        calculating={calculating}
        handleCalculate={handleCalculate}
        resetResults={resetResults}
      />

      {!hasCalculated && previewList.length > 0 && (
        <PointagesPreviewCard
          previewList={previewList}
          dateRangeLabel={dateRangeLabel}
        />
      )}

      {hasCalculated && (
        <PointagesResultsSection
          results={results}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          statusCounts={statusCounts}
          resultStats={resultStats}
          viewMode={viewMode}
          setViewMode={setViewMode}
          filteredResults={filteredResults}
          paginatedResults={paginatedResults}
          tableSearch={tableSearch}
          setTableSearch={setTableSearch}
          handleCalculate={handleCalculate}
          calculating={calculating}
          setReportDialog={setReportDialog}
          absenceReasons={absenceReasons}
          openAssignDialog={openAssignDialog}
          openManualPunchDialog={openManualPunchDialog}
          openAbsenceDialog={openAbsenceDialog}
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          summaryRows={summaryRows}
          expandedEmployees={expandedEmployees}
          toggleEmployeeExpand={toggleEmployeeExpand}
          runCalculation={runCalculation}
          dateFrom={dateFrom}
          dateTo={dateTo}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalPages={totalPages}
        />
      )}

      <AssignPlanningDialog
        open={!!assignDialog}
        onClose={() => setAssignDialog(null)}
        employee={assignDialog}
        schedules={schedules}
        assignScheduleId={assignScheduleId}
        setAssignScheduleId={setAssignScheduleId}
        assignStartDate={assignStartDate}
        setAssignStartDate={setAssignStartDate}
        assignEndDate={assignEndDate}
        setAssignEndDate={setAssignEndDate}
        assigning={assigning}
        onAssign={handleAssign}
      />

      <ManualPunchDialog
        open={!!manualPunchDialog}
        onClose={() => setManualPunchDialog(null)}
        entry={manualPunchDialog}
        manualPunchTime={manualPunchTime}
        setManualPunchTime={setManualPunchTime}
        manualPunchDir={manualPunchDir}
        setManualPunchDir={setManualPunchDir}
        manualPunchReason={manualPunchReason}
        setManualPunchReason={setManualPunchReason}
        addingPunch={addingPunch}
        onAdd={handleAddManualPunch}
      />

      <AbsenceReasonDialog
        open={!!absenceDialog}
        onClose={() => setAbsenceDialog(null)}
        entry={absenceDialog}
        absenceReasons={absenceReasons}
        selectedReasonId={selectedReasonId}
        setSelectedReasonId={setSelectedReasonId}
        assigningAbsence={assigningAbsence}
        onAssign={handleAssignAbsenceReason}
      />

      <ReportDialog
        open={reportDialog}
        onOpenChange={setReportDialog}
        companyInfo={companyInfo}
        dateRangeLabel={dateRangeLabel}
        reports={reports}
        selectedReportId={selectedReportId}
        setSelectedReportId={setSelectedReportId}
        congesLoading={congesQ.isPending && reportDialog}
        exportingPDF={exportingPDF}
        exportCSV={handleExportCSV}
        exportExcel={handleExportExcel}
        exportPDF={handleExportPDF}
      />

    </div>
  );
};

export default CalculRapport;
